import React, { useState, useEffect, useRef } from 'react';
import { Chart } from 'chart.js/auto';
import './Console.css';

// Import sub-components
import Sidebar from './Sidebar';
import DashboardView from './Dashboard';
import RoutingRules from './RoutingRules';
import Team from './Team';
import Reports from './Reports';
import SettingsView from './SettingsView';
import ActionRequiredPanel from './ActionRequiredPanel';
import SuccessMessage from './SuccessMessage';

// Import API service
import api from '../services/api';

const Console = () => {
  // Navigation state
  const [currentView, setCurrentView] = useState('dashboard');
  
  // Data state
  const [leads, setLeads] = useState([]);
  const [team, setTeam] = useState([]);
  const [rules, setRules] = useState({ language: [], schedule: [], source: [] });
  
  // ACTION ITEMS WITH SAMPLE DATA FOR TESTING
  const [actionItems, setActionItems] = useState([]);
  
  const [metrics, setMetrics] = useState(null);
  
  // UI state
  const [actionPanelExpanded, setActionPanelExpanded] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
  try {
    setLoading(true);
    
    // Fetch real data from backend
    const [leadsData, teamData, rulesData, actionsData, metricsData] = await Promise.all([
      api.leads.list(),
      api.team.list(),
      api.routingRules.list(),
      api.actions.listPending(),
      api.metrics.getDashboard()
    ]);

    setLeads(leadsData);
    setTeam(teamData);
    setRules(rulesData);
    setActionItems(actionsData);
    setMetrics(metricsData);
    
    setLoading(false);
    
  } catch (error) {
    console.error('Failed to load data:', error);
    showSuccess('Failed to load data. Please refresh.', 'error');
    setLoading(false);
  }
};
  // Success message helper
  const showSuccess = (message, type = 'success') => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
  };

  const closeSuccessMessage = () => {
    setShowSuccessMessage(false);
  };

  // Auto-expand action panel when there are items
  useEffect(() => {
    if (actionItems.length > 0 && !actionPanelExpanded) {
      // Auto-expand after a brief delay so user notices the change
      setTimeout(() => {
        setActionPanelExpanded(true);
      }, 500);
    }
  }, [actionItems.length]);

  if (loading) {
    return (
      <div className="console-loading">
        <div className="spinner"></div>
        <p>Loading Collab AI Console...</p>
      </div>
    );
  }

  return (
    <div className="console-container">
      <Sidebar 
        currentView={currentView}
        setCurrentView={setCurrentView}
      />

      <div className={`main-content ${actionPanelExpanded ? 'panel-expanded' : ''}`}>
        {currentView === 'dashboard' && (
          <DashboardView
            leads={leads}
            setLeads={setLeads}
            team={team}
            metrics={metrics}
            onSuccess={showSuccess}
          />
        )}

        {currentView === 'routing' && (
          <RoutingRules 
            rules={rules}
            setRules={setRules}
            onSuccess={showSuccess}
          />
        )}

        {currentView === 'team' && (
          <Team 
            team={team}
            setTeam={setTeam}
            onSuccess={showSuccess}
          />
        )}

        {currentView === 'reports' && (
          <Reports 
            team={team}
            onSuccess={showSuccess}
          />
        )}

        {currentView === 'settings' && (
          <SettingsView 
            onSuccess={showSuccess}
          />
        )}
      </div>

      <ActionRequiredPanel 
        actionItems={actionItems}
        setActionItems={setActionItems}
        expanded={actionPanelExpanded}
        setExpanded={setActionPanelExpanded}
        onSuccess={showSuccess}
      />

      {showSuccessMessage && (
        <SuccessMessage 
          message={successMessage} 
          onClose={closeSuccessMessage}
        />
      )}
    </div>
  );
};

export default Console;